/**
 * Auth/helper.js
 *
 * ── CHANGES ──────────────────────────────────────────────────────────────────
 * Added: triggerSendOtp()   → POST /send-otp
 * Added: triggerVerifyOtp() → POST /verify-otp
 * Kept:  triggerSignin, triggerSignup, authenticate, signout, isAuthenticated
 *        (all unchanged — existing flows are unaffected)
 */

import { API } from "../../backend";

// ─── Existing (unchanged) ─────────────────────────────────────────────────────

export const triggerSignin = (user) => {
  return fetch(`${API}signin`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(user),
  })
    .then((response) => {
      console.log(response);
      return response.json();
    })
    .catch((e) => {
      console.log(e);
    });
};

export const triggerSignup = (user) => {
  return fetch(`${API}signup`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify(user),
  })
    .then((response) => {
      console.log(response);
      return response.json();
    })
    .catch((e) => {
      console.log(e);
    });
};

export const authenticate = (data) => {
  if (typeof window != undefined) {
    localStorage.setItem("token", JSON.stringify(data));
  } else {
    return false;
  }
};

export const signout = (next) => {
  if (typeof window != undefined) {
    localStorage.removeItem("token");
    next();
  }
  return fetch(`${API}signout`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  })
    .then((response) => {
      return response.json();
    })
    .catch((e) => {
      return e.json();
    });
};

export const isAuthenticated = () => {
  if (typeof window === "undefined") return false;
  
  try {
    const token = localStorage.getItem("token");
    if (!token) return false;
    return JSON.parse(token);
  } catch {
    localStorage.removeItem("token"); // clear corrupted data
    return false;
  }
};

// ─── NEW: OTP Auth Helpers ────────────────────────────────────────────────────

/**
 * Sends OTP to the given mobile number (via WhatsApp or SMS, decided by backend).
 * @param {string|number} MobileNo - local number without country code
 * @param {string} [dialCode] - country dial code e.g. "91"
 * @returns {Promise<{ message?: string, channel?: "whatsapp" | "sms", error?: string }>}
 */
export const triggerSendOtp = async (MobileNo, dialCode) => {
  try {
    const body = { MobileNo: String(MobileNo) };
    if (dialCode) body.dialCode = String(dialCode);

    const response = await fetch(`${API}send-otp`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
    return await response.json();
  } catch (e) {
    console.error("[triggerSendOtp]", e);
    return { error: "Network error. Please try again." };
  }
};

/**
 * Verifies OTP for the given mobile number.
 * On success, returns { token, user } which should be passed to authenticate().
 * @param {string|number} MobileNo
 * @param {string} otp
 * @returns {Promise<{ token?: string, user?: object, error?: string }>}
 */
export const triggerVerifyOtp = async (MobileNo, otp) => {
  try {
    const response = await fetch(`${API}verify-otp`, {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ MobileNo: String(MobileNo), otp: String(otp) }),
    });
    return await response.json();
  } catch (e) {
    console.error("[triggerVerifyOtp]", e);
    return { error: "Network error. Please try again." };
  }
};

/**
 * Registers a new OTP-verified user (no password).
 * Called from /signup after OTP verification confirmed isNewUser: true
 * @param {{ Name, EmailId, Address, MobileNo }} userData
 * @returns {Promise<{ token?, user?, error? }>}
 */
export const triggerRegister = async (userData) => {
  try {
    const response = await fetch(`${API}register`, {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(userData),
    });
    return await response.json();
  } catch (e) {
    console.error("[triggerRegister]", e);
    return { error: "Network error. Please try again." };
  }
};

/**
 * Fetches active country codes from DB for the dial code dropdown.
 * @returns {Promise<Array<{ countryName, countryCode, dialCode, flag, mobileLength }>>}
 */
export const triggerGetCountryCodes = async () => {
  try {
    const response = await fetch(`${API}country-codes`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    return await response.json();
  } catch (e) {
    console.error("[triggerGetCountryCodes]", e);
    return [];
  }
};